import { useState, useRef } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, X } from "lucide-react";
import { api, API } from "@/lib/api";

/**
 * Drag-drop image uploader. Uploads via /api/admin/upload and returns the
 * absolute image URL via the `onUploaded(url)` callback.
 *
 * Props:
 *   value        current image URL to preview (or empty)
 *   onUploaded   (url) => void — called after successful upload
 *   onCleared    () => void — called when user removes the image
 */
export default function ImageDropzone({ value = "", onUploaded, onCleared, label = "Drop an image or click to browse" }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const resolveUrl = (rel) => (rel.startsWith("http") ? rel : `${API.replace(/\/api$/, "")}${rel}`);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Only image files are allowed");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Max file size is 12 MB");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/admin/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const absUrl = resolveUrl(data.url);
      onUploaded?.(absUrl);
      toast.success("Uploaded");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div>
      <div
        data-testid="image-dropzone"
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative border-2 border-dashed transition-colors cursor-pointer ${dragging ? "border-[#FF4500] bg-[#FF4500]/[0.06]" : "border-white/15 bg-[#0A0A0A]"} hover:border-white/40`}
      >
        {value ? (
          <div className="relative">
            <img src={value} alt="preview" className="w-full max-h-56 object-contain bg-[#0A0A0A]" />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onCleared?.(); }}
              className="absolute top-2 right-2 w-8 h-8 bg-[#0A0A0A]/80 border border-white/20 flex items-center justify-center text-white/80 hover:text-[#FF4500] hover:border-[#FF4500]"
              aria-label="Remove image"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A0A0A]/85 to-transparent p-3 text-center">
              <p className="text-[10px] uppercase tracking-widest text-white/70">Click / drop to replace</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
            {uploading ? (
              <>
                <Loader2 className="w-6 h-6 text-[#FF4500] animate-spin mb-3" />
                <p className="text-sm text-white/70">Uploading…</p>
              </>
            ) : (
              <>
                <UploadCloud className="w-8 h-8 text-white/60 mb-3" />
                <p className="text-sm text-white/80">{label}</p>
                <p className="text-[10px] uppercase tracking-widest text-white/40 mt-2">JPG · PNG · WebP · Max 12 MB</p>
              </>
            )}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          disabled={uploading}
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="image-dropzone-input"
        />
      </div>
    </div>
  );
}
